import { useRef } from 'react';
import { useDialogFocus } from './useDialogFocus';
import { X, UserRound, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { AdminRelationships, AdminPerson } from './api';
import {
  directOwners,
  entityName,
  personPractices,
  practiceCompanies,
  type EntityReference,
} from './relationships';
import { profileReviewLabels } from '../advisers/profileReview';

const date = (value: string | null) => (value ? new Date(value).toLocaleDateString() : '—');
export default function AdminEntityDetail({
  data,
  selected,
  onSelect,
  onClose,
  onImpersonate,
  onManage,
  onReview,
}: {
  data: AdminRelationships;
  selected: EntityReference;
  onSelect: (ref: EntityReference) => void;
  onClose: () => void;
  onImpersonate: (ref: EntityReference, candidates: AdminPerson[]) => void;
  onManage: (kind: 'users' | 'companies', search: string) => void;
  onReview: (practiceId: string, tab: 'practices' | 'profiles') => void;
}) {
  const dialog = useRef<HTMLElement>(null);
  useDialogFocus(dialog, onClose);
  const user = selected.scope === 'user' ? data.users.find((p) => p.id === selected.id) : null;
  const company =
    selected.scope === 'company' ? data.companies.find((p) => p.id === selected.id) : null;
  const practice =
    selected.scope === 'practice' ? data.practices.find((p) => p.id === selected.id) : null;
  const candidates = user
    ? [user]
    : company
      ? data.users.filter((person) =>
          data.memberships.some(
            (m) =>
              m.companyId === company.id &&
              m.userId === person.id &&
              !m.adviserPracticeId &&
              m.active
          )
        )
      : practice
        ? data.users.filter((person) =>
            data.practiceMembers.some((m) => m.practiceId === practice.id && m.userId === person.id)
          )
        : [];
  const ownerFirst = [...candidates].sort((a, b) => {
    if (practice)
      return Number(b.id === practice.ownerUserId) - Number(a.id === practice.ownerUserId);
    if (company) {
      const owners = directOwners(data, company.id);
      return Number(owners.some((p) => p.id === b.id)) - Number(owners.some((p) => p.id === a.id));
    }
    return 0;
  });
  const connection = (ref: EntityReference, description: string, key: string) => (
    <div className="admin-connection-row" key={key}>
      <div>
        <button className="admin-related-button" type="button" onClick={() => onSelect(ref)}>
          {entityName(data, ref)} <ArrowRight size={13} />
        </button>
        <span>{description}</span>
      </div>
    </div>
  );
  return (
    <div
      className="admin-detail-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <aside
        className="admin-detail"
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-detail-name"
      >
        <div className="admin-section-heading">
          <div>
            <p className="adviser-eyebrow">
              {selected.scope === 'practice' ? 'Adviser firm' : selected.scope}
            </p>
            <h2 id="admin-detail-name">{entityName(data, selected)}</h2>
            {user && <p>{user.email}</p>}
          </div>
          <button
            className="admin-icon-button"
            aria-label="Close record details"
            type="button"
            onClick={onClose}
          >
            <X size={21} />
          </button>
        </div>
        {!user && !company && !practice ? (
          <p className="adviser-notice">
            This record is no longer available. Refresh the directory to continue.
          </p>
        ) : (
          <>
            <div className="admin-actions">
              <button
                className="adviser-button"
                type="button"
                disabled={!candidates.length}
                onClick={() => onImpersonate(selected, ownerFirst)}
              >
                <UserRound size={15} />
                Open support session
              </button>
              {user && (
                <button
                  className="adviser-button adviser-button-secondary"
                  type="button"
                  onClick={() => onManage('users', user.email)}
                >
                  Manage permissions
                </button>
              )}
              {company && (
                <button
                  className="adviser-button adviser-button-secondary"
                  type="button"
                  onClick={() => onManage('companies', company.name)}
                >
                  Company controls
                </button>
              )}
            </div>
            {!candidates.length && company && (
              <p className="adviser-muted">
                This workspace has no direct client account yet. Open the connected practice to view
                it as an adviser.
              </p>
            )}
            {user && (
              <>
                <section>
                  <h3>Account</h3>
                  <dl>
                    <dt>Access</dt>
                    <dd>{user.superUser ? 'Superuser' : 'Standard account'}</dd>
                    <dt>Personal Founder</dt>
                    <dd>
                      {user.isFounder
                        ? user.founderSubscriptionStatus || 'Status unavailable'
                        : 'No personal subscription'}
                    </dd>
                    <dt>Joined</dt>
                    <dd>{date(user.createdAt)}</dd>
                    {user.title && (
                      <>
                        <dt>Title</dt>
                        <dd>{user.title}</dd>
                      </>
                    )}
                  </dl>
                </section>
                <section>
                  <h3>Company access</h3>
                  <p>
                    Direct memberships remain separate from the access an adviser practice grants.
                  </p>
                  {data.memberships
                    .filter((m) => m.userId === user.id)
                    .map((m) =>
                      connection(
                        { scope: 'company', id: m.companyId },
                        `${m.adviserPracticeId ? 'Delegated by ' + entityName(data, { scope: 'practice', id: m.adviserPracticeId }) : 'Direct membership'} · ${m.role} · ${m.active ? 'Active' : 'Access ended'}`,
                        m.id
                      )
                    )}
                  {!data.memberships.some((m) => m.userId === user.id) && (
                    <p>No company memberships.</p>
                  )}
                </section>
                <section>
                  <h3>Adviser practices</h3>
                  {personPractices(data, user.id).map((p) =>
                    connection(
                      { scope: 'practice', id: p.id },
                      `${data.practiceMembers.find((m) => m.practiceId === p.id && m.userId === user.id)?.role} · ${p.status}`,
                      p.id
                    )
                  )}
                  {!personPractices(data, user.id).length && (
                    <p>No adviser practice memberships.</p>
                  )}
                </section>
                {data.assignments.some((a) => a.userId === user.id) && (
                  <section>
                    <h3>Assigned client work</h3>
                    {data.assignments
                      .filter((a) => a.userId === user.id)
                      .map((a) =>
                        connection(
                          { scope: 'company', id: a.companyId },
                          `Assigned by ${entityName(data, { scope: 'practice', id: a.practiceId })}`,
                          a.id
                        )
                      )}
                  </section>
                )}
              </>
            )}
            {company && (
              <>
                <section>
                  <h3>Company context</h3>
                  <dl>
                    <dt>Industry</dt>
                    <dd>{company.industry || 'Not specified'}</dd>
                    <dt>Company size</dt>
                    <dd>{company.size || 'Not specified'}</dd>
                    <dt>Entitlement</dt>
                    <dd>
                      {company.isDemo
                        ? 'Private demo · no paid subscription'
                        : company.subscriptionTier}
                    </dd>
                    <dt>Ownership</dt>
                    <dd>
                      {company.provisionalPracticeId
                        ? 'Client ownership invitation pending'
                        : 'Independent company'}
                    </dd>
                    <dt>Created</dt>
                    <dd>{date(company.createdAt)}</dd>
                  </dl>
                </section>
                <section>
                  <h3>Direct owners</h3>
                  {directOwners(data, company.id).map((owner) =>
                    connection({ scope: 'user', id: owner.id }, owner.email, owner.id)
                  )}
                  {!directOwners(data, company.id).length && (
                    <p>
                      {company.isDemo
                        ? 'The connected adviser practice manages this private demo.'
                        : 'No direct client owner yet.'}
                    </p>
                  )}
                </section>
                <section>
                  <h3>People with access</h3>
                  {data.memberships
                    .filter((m) => m.companyId === company.id)
                    .map((m) =>
                      connection(
                        { scope: 'user', id: m.userId },
                        `${m.adviserPracticeId ? 'Adviser access' : 'Direct membership'} · ${m.role} · ${m.active ? 'Active' : 'Access ended'}`,
                        m.id
                      )
                    )}
                </section>
                <section>
                  <h3>Adviser relationships</h3>
                  {company.demoPracticeId &&
                    connection(
                      { scope: 'practice', id: company.demoPracticeId },
                      'Practice-owned private demo',
                      company.demoPracticeId
                    )}
                  {data.clientLinks
                    .filter((l) => l.companyId === company.id)
                    .map((link) =>
                      connection(
                        { scope: 'practice', id: link.practiceId },
                        `${link.status === 'ownership_pending' ? 'Awaiting client ownership' : link.status === 'pending' ? 'Awaiting client consent' : link.status === 'revoked' ? 'Delegation revoked · direct ownership retained' : 'Approved delegation'}${link.acceptedAt ? ' · accepted ' + date(link.acceptedAt) : ''}`,
                        link.id
                      )
                    )}
                  {!company.demoPracticeId &&
                    !data.clientLinks.some((l) => l.companyId === company.id) && (
                      <p>No adviser practice connected.</p>
                    )}
                </section>
              </>
            )}
            {practice && (
              <>
                <section>
                  <h3>Practice status</h3>
                  <dl>
                    <dt>Application</dt>
                    <dd>{practice.status}</dd>
                    <dt>Public profile</dt>
                    <dd>
                      {practice.profile
                        ? profileReviewLabels[practice.profile.reviewStage]
                        : 'No profile draft'}
                    </dd>
                    <dt>Publication</dt>
                    <dd>
                      {practice.profile?.publiclyListed
                        ? 'Publicly listed'
                        : practice.profile?.isPublished
                          ? 'Hidden by practice status'
                          : 'Private'}
                    </dd>
                    <dt>Homepage</dt>
                    <dd>
                      {practice.profile?.publiclyListed && practice.profile.featuredOrder !== null
                        ? `Featured · position ${practice.profile.featuredOrder + 1}`
                        : 'Not featured'}
                    </dd>
                    <dt>Applied</dt>
                    <dd>{date(practice.createdAt)}</dd>
                  </dl>
                  {practice.reviewReason && (
                    <p className="adviser-notice">{practice.reviewReason}</p>
                  )}
                  <div className="admin-actions">
                    <button
                      className="adviser-button adviser-button-secondary"
                      type="button"
                      onClick={() => onReview(practice.id, 'practices')}
                    >
                      Review application
                    </button>
                    <button
                      className="adviser-button adviser-button-secondary"
                      type="button"
                      onClick={() => onReview(practice.id, 'profiles')}
                    >
                      Review profile
                    </button>
                    {practice.profile?.publiclyListed && (
                      <Link className="adviser-text-link" to={`/advisers/${practice.profile.slug}`}>
                        View public profile
                      </Link>
                    )}
                  </div>
                </section>
                <section>
                  <h3>Practice owner</h3>
                  {connection(
                    { scope: 'user', id: practice.ownerUserId },
                    'Owner · manages the firm and billing',
                    practice.ownerUserId
                  )}
                </section>
                <section>
                  <h3>Practice team</h3>
                  {data.practiceMembers
                    .filter((m) => m.practiceId === practice.id)
                    .map((m) =>
                      connection(
                        { scope: 'user', id: m.userId },
                        `${m.role} · ${m.role === 'staff' ? `${data.assignments.filter((a) => a.practiceId === practice.id && a.userId === m.userId).length} assigned companies` : 'All practice companies'}`,
                        m.id
                      )
                    )}
                </section>
                <section>
                  <h3>Client companies and demos</h3>
                  {practiceCompanies(data, practice.id).map((c) =>
                    connection(
                      { scope: 'company', id: c.id },
                      c.isDemo
                        ? 'Private reusable demo'
                        : data.clientLinks.find(
                            (l) =>
                              l.practiceId === practice.id &&
                              l.companyId === c.id &&
                              l.status !== 'revoked'
                          )?.status || 'Company',
                      c.id
                    )
                  )}
                  {!practiceCompanies(data, practice.id).length && (
                    <p>No active client companies or demos.</p>
                  )}
                </section>
                {data.clientLinks.some(
                  (l) => l.practiceId === practice.id && l.status === 'revoked'
                ) && (
                  <section>
                    <h3>Former client relationships</h3>
                    {data.clientLinks
                      .filter((l) => l.practiceId === practice.id && l.status === 'revoked')
                      .map((l) =>
                        connection(
                          { scope: 'company', id: l.companyId },
                          'Delegation revoked',
                          l.id
                        )
                      )}
                  </section>
                )}
              </>
            )}
          </>
        )}
      </aside>
    </div>
  );
}
